import { useEffect, useState } from 'react'
import { Box, Button, Flex, Heading, HStack, Text } from '@chakra-ui/react';
import { PageContainer } from '../components/shared/PageContainer'
import { StepRail } from '../components/guidedList/StepRail'
import { WIZARD_STEPS } from '../components/guidedList/stepMeta'
import { ContestStep } from '../components/guidedList/steps/ContestStep'
import { LocalityStep } from '../components/guidedList/steps/LocalityStep'
import { GoalStep } from '../components/guidedList/steps/GoalStep'
import { AudienceStep } from '../components/guidedList/steps/AudienceStep'
import { ReviewStep } from '../components/guidedList/steps/ReviewStep'
import { GUIDED_SPEC_DEFAULTS, officeLabel, type GuidedListSpec } from '../types/guidedList'
import type { FilterGroup } from '../types/contactList'
import { useIsMobile } from '../hooks/useIsMobile'
import { useCompileGuidedList } from '../hooks/useGuidedList'

export function GuidedListWizardPage() {
  const isMobile = useIsMobile()
  const [step, setStep] = useState(0)
  const [maxReached, setMaxReached] = useState(0)
  const [spec, setSpec] = useState<GuidedListSpec>(GUIDED_SPEC_DEFAULTS)
  const compile = useCompileGuidedList()
  const [groups, setGroups] = useState<FilterGroup[] | null>(null)
  const [compileError, setCompileError] = useState<unknown>(null)

  useEffect(() => {
    if (WIZARD_STEPS[step].key !== 'review') return
    setGroups(null)
    setCompileError(null)
    compile.mutate(spec, { onSuccess: setGroups, onError: setCompileError })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  const goNext = () => {
    const next = Math.min(step + 1, WIZARD_STEPS.length - 1)
    setStep(next)
    setMaxReached(m => Math.max(m, next))
  }
  const goBack = () => setStep(s => Math.max(0, s - 1))
  const patch = (p: Partial<GuidedListSpec>) => setSpec(prev => ({ ...prev, ...p }))

  const isLast = step === WIZARD_STEPS.length - 1
  // Required-field gate: don't let an empty locality through, or Review compiles the whole
  // committee-Dem universe. State + district must be set before leaving the Locality step.
  const canAdvance = WIZARD_STEPS[step].key !== 'locality' || (!!spec.state && !!spec.districtCode)

  return (
    <PageContainer>
      <Heading size="md" mb={1}>Build for your candidate</Heading>
      <Text color="gray.600" fontSize="sm" mb={4}>Guided list · {WIZARD_STEPS[step].label} · step {step + 1} of {WIZARD_STEPS.length}</Text>

      <Flex direction={isMobile ? 'column' : 'row'} gap={6} align="flex-start">
        {!isMobile && <StepRail current={step} maxReached={maxReached} onJump={setStep} />}

        <Box flex="1" minW={0} w="full">
          <Box borderWidth="1px" borderRadius="lg" p={5} minH="240px">
            {WIZARD_STEPS[step].key === 'contest' && <ContestStep spec={spec} patch={patch} />}
            {WIZARD_STEPS[step].key === 'locality' && <LocalityStep spec={spec} patch={patch} />}
            {WIZARD_STEPS[step].key === 'goal' && <GoalStep spec={spec} patch={patch} />}
            {WIZARD_STEPS[step].key === 'audience' && <AudienceStep spec={spec} patch={patch} />}
            {WIZARD_STEPS[step].key === 'review' && (
              <ReviewStep spec={spec} groups={groups} error={compileError}
                presetName={`Guided · ${officeLabel(spec.office)} ${spec.districtCode ?? ''}`.trim()} />
            )}
          </Box>

          <HStack mt={4} justify="space-between">
            <Button variant="ghost" onClick={goBack} disabled={step === 0}>‹ Back</Button>
            {!isLast && <Button colorPalette="brand" onClick={goNext} disabled={!canAdvance}>Next ›</Button>}
          </HStack>
        </Box>
      </Flex>
    </PageContainer>
  );
}
